import { useCallback, useEffect, useRef, useState } from 'react'

interface GameRowScroll {
  scrollRef: React.RefObject<HTMLDivElement>
  canScrollLeft: boolean
  canScrollRight: boolean
  scrollLeftBy: () => void
  scrollRightBy: () => void
}

/**
 * Tracks the horizontal scroll position of the game row so the lobby / track
 * overlay can show left/right arrows + edge fades and scroll by ~one viewport on
 * click. This is a desktop/mouse affordance — touch users keep native scrolling,
 * and the arrows/fades are hidden via CSS on coarse pointers.
 *
 * `recomputeDeps` should include anything that changes the row's content width
 * (e.g. the games list) so the arrow visibility re-evaluates.
 */
export function useGameRowScroll(recomputeDeps: readonly unknown[] = []): GameRowScroll {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const recompute = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    // 1px tolerance so sub-pixel rounding doesn't leave a stuck arrow.
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft < maxScroll - 1)
  }, [])

  const scrollByDirection = useCallback((direction: 1 | -1) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' })
  }, [])

  const scrollLeftBy = useCallback(() => {
    scrollByDirection(-1)
  }, [scrollByDirection])
  const scrollRightBy = useCallback(() => {
    scrollByDirection(1)
  }, [scrollByDirection])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    recompute()
    el.addEventListener('scroll', recompute, { passive: true })

    const resizeObserver = new ResizeObserver(recompute)
    resizeObserver.observe(el)
    window.addEventListener('resize', recompute)

    return () => {
      el.removeEventListener('scroll', recompute)
      resizeObserver.disconnect()
      window.removeEventListener('resize', recompute)
    }
  }, [recompute])

  // Re-evaluate when the content (games) changes, since that changes scrollWidth.
  useEffect(() => {
    recompute()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputeDeps is the caller-provided content-width trigger list
  }, recomputeDeps)

  return { scrollRef, canScrollLeft, canScrollRight, scrollLeftBy, scrollRightBy }
}
